import { uniqueId } from 'lodash';
import { useQuery, UseQueryOptions } from 'react-query';
import { authFetch } from '../../CommonUtils/authFetch';
import { urlWithParams } from '../../CommonUtils/urlWithParams';
import { dateToISO8601 } from '../helpers/dates';
import { PerformancePeriodDTO } from '../types';
import { SERIES_COLORS } from '../../CommonComponents/OPL/charts/charts.constants';
import { QueryFeature } from '../../../config/queryClient';

export interface Performance {
  name: string;
  value: number | null;
}

export interface TotalsDTO {
  name: string;
  endingMarketValue: number;
  performances: Performance[];
}

export interface AssetDetailDTO {
  name: string;
  endingMarketValue: number;
  performances: Performance[];
}

export interface BenchmarkDTO {
  name: string;
  performances: Performance[];
}

export interface AssetDTO {
  id: string;
  name: string;
  endingMarketValue: number;
  performances: Performance[];
  assetDetails: AssetDetailDTO[];
  benchmarks: BenchmarkDTO[];
  color: string | null;
}

export interface AssetTabDTO {
  name: string;
  rows: AssetDTO[];
}

export interface PerformanceDetailsDTO {
  periodLabels: string[];
  benchmarks: BenchmarkDTO[];
  totals: TotalsDTO;
  groups: AssetTabDTO[];
  defaultGroup: string | null;
}

interface FetchPerformanceDetailsParams {
  clientId: string;
  period?: PerformancePeriodDTO;
  startDate?: string;
  endDate?: string;
  accountId: string;
  groupId: string;
}

const fetchPerformanceDetails = async ({
  clientId,
  period,
  startDate,
  endDate,
  accountId,
  groupId,
}: FetchPerformanceDetailsParams): Promise<PerformanceDetailsDTO> => {
  const response: PerformanceDetailsDTO = await authFetch(
    urlWithParams(
      '/Consumer/OrionAccountsWidgets/PerformanceDetails',
      {
        clientId,
        period,
        startDate,
        endDate,
        accountId: accountId || null,
        groupId: groupId || null,
        localDate: dateToISO8601(),
      },
      { skipNulls: true },
    ),
  );

  return {
    ...response,
    groups: response.groups.map(group => ({
      ...group,
      rows: group.rows.map((row, i) => ({
        ...row,
        id: uniqueId(),
        color: row.color ?? SERIES_COLORS[i % SERIES_COLORS.length],
      })),
    })),
  };
};

interface UsePerformanceDetailsQueryParams {
  clientId: string;
  period?: PerformancePeriodDTO;
  startDate: string;
  endDate: string;
  accountId: string;
  groupId: string;
}

export const PERFORMANCE_DETAILS_QUERY_KEY = 'performanceDetails';

export const usePerformanceDetailsQuery = (
  {
    clientId,
    period,
    startDate: _startDate,
    endDate: _endDate,
    accountId,
    groupId,
  }: UsePerformanceDetailsQueryParams,
  options?: UseQueryOptions<PerformanceDetailsDTO, Error>,
) => {
  const startDate = period === undefined ? _startDate : undefined;
  const endDate = period === undefined ? _endDate : undefined;

  return useQuery(
    [
      QueryFeature.AccountPerformanceReporting,
      PERFORMANCE_DETAILS_QUERY_KEY,
      clientId,
      period,
      startDate,
      endDate,
      accountId,
      groupId,
    ],
    () => fetchPerformanceDetails({ clientId, period, startDate, endDate, accountId, groupId }),
    {
      retry: false,
      ...(period === undefined ? { cacheTime: 0 } : {}), // Do not cache custom ranges
      ...options,
    },
  );
};
